import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { DEEP_COMMENTING_REDESIGN_TOUR_ID } from 'collaboration-sidebar/features/deepCommenting/constants/deepCommentingOnboarding';
import I18n from 'I18n';
import { useEffect, useRef, useState } from 'react';
import { IS_COMMENT_MODE_ACTIVE } from '../constants/CollaborationSidebarStateProperties';
import CollaborationSidebarStateContext from '../contexts/CollaborationSidebarStateContext';
import UserSettingsProvider from '../contexts/UserSettingsProvider';
import usePersistPropsToCollaborationSidebarState from '../hooks/usePersistPropsToCollaborationSidebarState';
import { showWarningNotification } from '../utils/notifications'; // @ts-expect-error no-type

import { getIsCommentingUrl } from '../utils/urls'; // @ts-expect-error no-type

import AsyncOnboardingTour from 'collaboration-sidebar/features/deepCommenting/components/AsyncOnboardingTour'; // @ts-expect-error no-type

import CommentModeIndicator from 'collaboration-sidebar/features/deepCommenting/components/CommentModeIndicator';
import { CommentObjectType, FEATURES } from '../constants';
import CollaborationSidebarPropsContext from '../contexts/CollaborationSidebarPropsContext';
import useEmbeddedComments from '../features/deepCommenting/hooks/useEmbeddedComments';
import useHasUnreadComments from '../hooks/useHasUnreadComments';
import useSidebarIframeHandlers from '../hooks/useSidebarIframeHandlers'; // @ts-expect-error no-type

import useConfirmDeleteCommentModal from 'collaboration-sidebar/hooks/modals/useConfirmDeleteCommentModal';
import useNotificationSettingsModal from 'collaboration-sidebar/hooks/modals/useNotificationSettingsModal'; // @ts-expect-error no-type

import useTaskDeleteModal from 'collaboration-sidebar/hooks/modals/useTaskDeleteModal'; // @ts-expect-error no-type

import useCampaignCreatePanel from 'collaboration-sidebar/hooks/modals/useCampaignCreatePanel';
import { FLOATING_SIDEBAR_ALL } from '../constants/gates';
import useSidebarOpenCloseHandlers from '../hooks/useSidebarOpenCloseHandlers';
import useHasSidebarPermission from '../hooks/useHasSidebarPermission';
import { isUngated } from '../utils/auth';
import AsyncMessageHandlers from './MessageHandlers/AsyncMessageHandlers';
import Modals from './Modals';
import EmbeddedCommentSetup from './EmbeddedCommentSetup';
import usePollForCommentsChanges from '../hooks/usePollForCommentsChanges';
import useOnboardingTour from '../hooks/useOnboardingTour';
import { COMMENT_ONBOARDING_TOUR_ID } from '../constants/tourIDs';
export default function ControlledCollaborationSidebarProvider(_ref) {
  let {
    appHub,
    children,
    pageContentRef: pageContentRefFromProps,
    collaborationSidebarState,
    withoutSidebar,
    onReady,
    isControlled = true
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["appHub", "children", "pageContentRef", "collaborationSidebarState", "withoutSidebar", "onReady", "isControlled"]);

  const didEmitOnReady = useRef(false);
  const hasShownNoPermissionWarning = useRef(false);
  const pageContentRefNotFromProps = useRef(null);
  const sendMessageRef = useRef();
  const onMessageRef = useRef(() => {});
  const [messageHandlersReady, setMessageHandlersReady] = useState(false);
  useEffect(() => {
    if (messageHandlersReady && onReady && !didEmitOnReady.current) {
      didEmitOnReady.current = true;
      onReady();
    }
  }, [messageHandlersReady, onReady]);
  const {
    objectType,
    objectId,
    onUpdateCollaborationSidebarState,
    floating: floatingFromProps,
    enabledFeatures,
    openLeftEditor,
    closeLeftEditor,
    onCommentsUpdated,
    onCampaignAssociationSuccess,
    embeddedCommentsProps,
    commentingDisabled
  } = rest;
  const floating = isUngated(FLOATING_SIDEBAR_ALL) || floatingFromProps;
  const collaborationSidebarStateWithPersistedProps = usePersistPropsToCollaborationSidebarState({
    collaborationSidebarState,
    onUpdateCollaborationSidebarState,
    collaborationSidebarProps: rest
  });
  const expandableSidebarRef = useRef(null);
  const iframeHandlerProps = useSidebarIframeHandlers({
    withoutSidebar,
    isOpen: collaborationSidebarStateWithPersistedProps.isOpen,
    sendMessageRef
  });
  const pageContentRef = pageContentRefFromProps || pageContentRefNotFromProps;
  const hasSidebarPermission = useHasSidebarPermission({
    appHub
  });
  useEffect(() => {
    if (!hasShownNoPermissionWarning.current && !hasSidebarPermission && getIsCommentingUrl()) {
      showWarningNotification({
        title: I18n.text('collaborationSidebar.notifications.failure.missingPermissionCommentingTitle'),
        message: I18n.text('collaborationSidebar.notifications.failure.missingPermissionCommentingBody'),
        sticky: true
      });
      hasShownNoPermissionWarning.current = true;
    }
  }, [hasSidebarPermission]);
  const {
    hasUnreadComments,
    unreadCommentsCount,
    onCommentsRead,
    markCommentAsRead,
    lastReadTimestamp,
    lastReadTimestampLoading,
    refetchComments
  } = useHasUnreadComments(objectType, objectId);
  const {
    isCommentModeActive,
    isEmbeddedCommentingOnboardingEnabled
  } = collaborationSidebarStateWithPersistedProps;
  usePollForCommentsChanges({
    objectId,
    objectType,
    onUpdateCollaborationSidebarState,
    hasSidebarPermission,
    isOpen: collaborationSidebarStateWithPersistedProps.isOpen,
    assetsLastUpdated: collaborationSidebarStateWithPersistedProps.assetsLastUpdated
  });
  /**
   * Campaigns in the sidebar is not being actively worked on.
   * We're keeping it in the codebase for now as it may be
   * prioritized with ongoing campaign templates improvements.
   */

  const enabledFeaturesWithoutCampaigns = enabledFeatures ? enabledFeatures.filter(feature => feature !== FEATURES.CAMPAIGNS) : enabledFeatures;
  const {
    handleClose
  } = useSidebarOpenCloseHandlers({
    onUpdateCollaborationSidebarState,
    openLeftEditor,
    closeLeftEditor
  });
  const {
    hideEmbeddedComments,
    setHideEmbeddedComments,
    setModuleReply,
    removeModuleReply,
    startCommentMode,
    cancelCommentMode
  } = useEmbeddedComments({
    collaborationSidebarState: collaborationSidebarStateWithPersistedProps,
    onUpdateCollaborationSidebarState,
    navigateTo: embeddedCommentsProps && embeddedCommentsProps.indicatorNavigateTo
  });

  const _useConfirmDeleteComm = useConfirmDeleteCommentModal(sendMessageRef.current),
        {
    handleMessage: handleConfirmDeleteCommentModalMessage,
    show: showDeleteCommentModal
  } = _useConfirmDeleteComm,
        confirmDeleteCommentModalProps = _objectWithoutPropertiesLoose(_useConfirmDeleteComm, ["handleMessage", "show"]);

  const _useNotificationSetti = useNotificationSettingsModal(),
        {
    handleMessage: handleNotificationSettingsModalMessage,
    show: showNotificationSettingsModal
  } = _useNotificationSetti,
        notificationSettingsModalProps = _objectWithoutPropertiesLoose(_useNotificationSetti, ["handleMessage", "show"]);

  const _useTaskDeleteModal = useTaskDeleteModal(sendMessageRef.current),
        {
    handleMessage: handleTaskUpdatePanelMessage,
    show: showTaskDeleteModal
  } = _useTaskDeleteModal,
        taskDeleteModalProps = _objectWithoutPropertiesLoose(_useTaskDeleteModal, ["handleMessage", "show"]);

  const _useCampaignCreatePan = useCampaignCreatePanel({
    sendMessage: sendMessageRef.current
  }),
        {
    handleMessage: handleCampaignCreatePanelMessage,
    show: showCampaignCreatePanel
  } = _useCampaignCreatePan,
        campaignCreatePanelProps = _objectWithoutPropertiesLoose(_useCampaignCreatePan, ["handleMessage", "show"]);

  const showCommentingTour = useOnboardingTour({
    featureIsVisible: (objectType === CommentObjectType.SOCIAL_BROADCAST || objectType === CommentObjectType.COMMENTING_SANDBOX) && !commentingDisabled,
    objectType,
    tourId: COMMENT_ONBOARDING_TOUR_ID
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [floating ? /*#__PURE__*/_jsx(AsyncMessageHandlers, {
      collaborationSidebarState: collaborationSidebarStateWithPersistedProps,
      onUpdateCollaborationSidebarState: onUpdateCollaborationSidebarState,
      handleTaskUpdatePanelMessage: handleTaskUpdatePanelMessage,
      handleCampaignCreatePanelMessage: handleCampaignCreatePanelMessage,
      handleConfirmDeleteCommentModalMessage: handleConfirmDeleteCommentModalMessage,
      handleNotificationSettingsModalMessage: handleNotificationSettingsModalMessage,
      onCommentsRead: onCommentsRead,
      markCommentAsRead: markCommentAsRead,
      handleClose: handleClose,
      refetchComments: refetchComments,
      onCommentsUpdated: onCommentsUpdated,
      preLoadIframe: iframeHandlerProps.preLoadIframe,
      onCampaignAssociationSuccess: onCampaignAssociationSuccess,
      setHideEmbeddedComments: setHideEmbeddedComments,
      hideEmbeddedComments: hideEmbeddedComments,
      startCommentMode: startCommentMode,
      sendMessageRef: sendMessageRef,
      setModuleReply: setModuleReply,
      removeModuleReply: removeModuleReply,
      onMessageRef: onMessageRef,
      setMessageHandlersReady: setMessageHandlersReady
    }) : null, /*#__PURE__*/_jsx(CollaborationSidebarPropsContext.Provider, {
      value: Object.assign({}, rest, {
        pageContentRef,
        enabledFeatures: enabledFeaturesWithoutCampaigns,
        hasSidebarPermission,
        expandableSidebarRef,
        hasUnreadComments,
        unreadCommentsCount,
        lastReadTimestamp,
        lastReadTimestampLoading,
        withoutSidebar,
        isModalOpen: showDeleteCommentModal || showTaskDeleteModal,
        iframe: iframeHandlerProps,
        onMessage: onMessageRef.current,
        messageHandlersReady,
        startCommentMode,
        cancelCommentMode,
        isControlled,
        floating
      }),
      children: /*#__PURE__*/_jsx(CollaborationSidebarStateContext.Provider, {
        value: {
          collaborationSidebarState: collaborationSidebarStateWithPersistedProps,
          onUpdateCollaborationSidebarState
        },
        children: /*#__PURE__*/_jsxs(UserSettingsProvider, {
          objectType: objectType,
          objectId: objectId,
          children: [floating && /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(EmbeddedCommentSetup, {}), /*#__PURE__*/_jsx(Modals, {
              confirmDeleteCommentModalProps: confirmDeleteCommentModalProps,
              taskDeleteModalProps: taskDeleteModalProps,
              campaignCreatePanelProps: campaignCreatePanelProps,
              notificationSettingsModalProps: notificationSettingsModalProps,
              showDeleteCommentModal: showDeleteCommentModal,
              showNotificationSettingsModal: showNotificationSettingsModal,
              showTaskDeleteModal: showTaskDeleteModal,
              showCampaignCreatePanel: showCampaignCreatePanel,
              withoutSidebar: !!withoutSidebar,
              isReady: messageHandlersReady,
              IFrameError: iframeHandlerProps.IFrameError
            })]
          }), children, isEmbeddedCommentingOnboardingEnabled && /*#__PURE__*/_jsx(AsyncOnboardingTour, {
            tourId: DEEP_COMMENTING_REDESIGN_TOUR_ID
          }), showCommentingTour && /*#__PURE__*/_jsx(AsyncOnboardingTour, {
            tourId: COMMENT_ONBOARDING_TOUR_ID
          }), isCommentModeActive && /*#__PURE__*/_jsx(CommentModeIndicator, {
            onCancel: () => {
              onUpdateCollaborationSidebarState({
                [IS_COMMENT_MODE_ACTIVE]: false
              });
            }
          })]
        })
      })
    })]
  });
}